// src/types/organization-config.ts
// Organization business configuration types for pricing and staff management
// These are stored in Organization.businessConfig field in DynamoDB
// RELEVANT FILES: src/components/app-sidebar/nav-team-add.vue, src/stores/organizationEdit.ts

import { z } from 'zod';

/**
 * Membership tier configuration
 */
export interface MembershipTier {
  readonly name: string; // e.g., "Basic", "Elite", "Premier"
  readonly monthlyPrice: number;
  readonly classesPerMonth: number | 'unlimited';
}

/**
 * Class pack configuration (one-time purchase)
 */
export interface ClassPack {
  readonly name: string; // e.g., "5-Class Pack"
  readonly numClasses: number;
  readonly price: number;
}

/**
 * Add-ons and additional fees
 */
export interface AddOns {
  readonly hrmRentalMonthly?: number; // Heart Rate Monitor rental per month
  readonly hrmPurchase?: number; // Heart Rate Monitor purchase price
  readonly lateCancelFee?: number; // Late cancellation fee
}

/**
 * Complete pricing configuration
 * All fields optional - validation only when provided
 * Uses readonly arrays for compatibility with API responses
 */
export interface PricingConfig {
  readonly membershipTiers?: readonly MembershipTier[];
  readonly classPacks?: readonly ClassPack[];
  readonly introOffer?: string; // Free-text description
  readonly addOns?: AddOns;
}

/**
 * Complete business configuration for an organization
 * This is stored in Organization.businessConfig field in DynamoDB
 */
export interface BusinessConfig {
  readonly pricing?: PricingConfig;
  readonly staff?: string; // Newline-separated names: "max\npeter\njohn"
}

/**
 * @deprecated Use BusinessConfig instead
 */
export type OrganizationConfig = BusinessConfig;

// ============ Zod Validation Schemas ============

/**
 * Membership tier validation schema
 */
export const membershipTierSchema = z
  .object({
    name: z
      .string()
      .min(1, 'Tier name is required')
      .max(50, 'Tier name must be less than 50 characters')
      .or(z.literal('')), // Allow empty string for new fields
    monthlyPrice: z
      .number()
      .min(0, 'Price must be positive')
      .max(10000, 'Price seems unreasonably high'),
    classesPerMonth: z.union([
      z.number().int('Must be a whole number').min(1, 'Must be at least 1 class'),
      z.literal('unlimited'),
    ]),
  })
  .refine(
    data => {
      // If name is empty, all fields should be empty (not yet filled)
      if (data.name === '') {
        return true;
      }
      // If name is filled, require valid values
      return data.name.length > 0 && data.monthlyPrice >= 0;
    },
    { message: 'Please fill in all fields or leave them empty', path: ['name'] },
  );

/**
 * Class pack validation schema
 */
export const classPackSchema = z
  .object({
    name: z
      .string()
      .min(1, 'Pack name is required')
      .max(50, 'Pack name must be less than 50 characters')
      .or(z.literal('')), // Allow empty string for new fields
    numClasses: z
      .number()
      .int('Must be a whole number')
      .min(1, 'Must include at least 1 class')
      .max(100, 'Number of classes seems unreasonably high'),
    price: z.number().min(0, 'Price must be positive').max(10000, 'Price seems unreasonably high'),
  })
  .refine(
    data => {
      // If name is empty, all fields should be empty (not yet filled)
      if (data.name === '') {
        return true;
      }
      // If name is filled, require valid values
      return data.name.length > 0 && data.numClasses >= 1 && data.price >= 0;
    },
    { message: 'Please fill in all fields or leave them empty', path: ['name'] },
  );

/**
 * Add-ons validation schema
 */
export const addOnsSchema = z.object({
  hrmRentalMonthly: z
    .number()
    .min(0, 'Rental price must be positive')
    .max(1000, 'Rental price seems unreasonably high')
    .optional(),
  hrmPurchase: z
    .number()
    .min(0, 'Purchase price must be positive')
    .max(1000, 'Purchase price seems unreasonably high')
    .optional(),
  lateCancelFee: z
    .number()
    .min(0, 'Fee must be positive')
    .max(1000, 'Fee seems unreasonably high')
    .optional(),
});

/**
 * Pricing configuration validation schema
 * Note: All fields are optional - validation only applies when data is provided
 */
export const pricingConfigSchema = z.object({
  membershipTiers: z
    .array(membershipTierSchema)
    .max(10, 'Too many membership tiers')
    .optional()
    .default([]),
  classPacks: z.array(classPackSchema).max(10, 'Too many class packs').optional().default([]),
  introOffer: z
    .string()
    .max(500, 'Description must be less than 500 characters')
    .optional()
    .or(z.literal('')),
  addOns: addOnsSchema.optional(),
});

/**
 * Business configuration validation schema
 */
export const businessConfigSchema = z.object({
  pricing: pricingConfigSchema.optional(),
  staff: z.string().max(500, 'Staff names list too long').optional(), // Newline-separated names: "max\npeter\njohn"
});

/**
 * @deprecated Use businessConfigSchema instead
 */
export const organizationConfigSchema = businessConfigSchema;
